import React from 'react'
import styled from 'react-emotion'
import { Block } from 'slate'
import { color } from 'stylist'
import { Element, createElementChild } from './ElementPlugin'

const onBackspace = (editor, next) => {
  const currentElement = editor.getElement()

  if (currentElement.type !== 'lineGroup' || currentElement.text) return next()
  if (!editor.isCursorAtStartingBoundary()) return next()

  editor.removePreviousElement(node => node.type === 'pageBreak')

  if (editor.isTouchingLineGroup()) {
    editor.moveToPreviousElement().removeNextElement()
    return false
  }
  return next()
}

const onDelete = (editor, next) => {
  const currentElement = editor.getElement()

  if (
    currentElement.type !== 'lineGroup' ||
    !editor.isCursorAtEndingBoundary(true)
  )
    return next()

  editor.removeNextElement(node => node.type === 'pageBreak')

  const nextElement = editor.getNextElement()
  const prevElement = editor.getPreviousElement()
  const isTouchingLineGroup =
    editor.isNodeType(nextElement, 'lineGroup') ||
    editor.isNodeType(prevElement, 'lineGroup')

  if (isTouchingLineGroup || currentElement.text) {
    if (editor.isNodeType(nextElement, 'lineGroup') && currentElement.text)
      return next()

    editor.moveToNextElement()

    if (!currentElement.text) {
      editor.removePreviousElement(node => !node.text)
    }
    return
  }
  return next()
}

const LineGroupPlugin = () => {
  return {
    schema: {
      blocks: {
        lineGroup: {
          parent: { object: 'document' },
          nodes: [
            {
              match: {
                type: 'line'
              },
              min: 1
            }
          ],
          normalize: (editor, { code, node, child, index }) => {
            switch (code) {
              case 'child_type_invalid':
                editor.replaceNodeByKey(
                  child.key,
                  createElementChild('line', child.text)
                )
                break
              default:
                break
            }
          }
        }
      }
    },
    onKeyDown(ev, editor, next) {
      if (ev.key === 'Backspace') {
        onBackspace(editor, next)
      } else if (ev.key === 'Delete') {
        onDelete(editor, next)
      } else {
        next()
      }
    },
    normalizeNode(node, editor, next) {
      const doc = editor.value.document
      const parent = doc.getParent(node.key)

      // Ensure all non lineGroups are preceded by and followed by a lineGroup
      if (doc.equals(parent) && node.type !== 'lineGroup') {
        editor.enforceAdjacentLineGroups(node)
      } else {
        next()
      }
    },
    renderNode(props, editor, next) {
      switch (props.node.type) {
        case 'lineGroup':
          return <LineGroup {...props} />
        case 'line':
          return <Line {...props} />
        default:
          return next()
      }
    },
    commands: {
      insertLineGroupBefore(editor, node) {
        editor.insertNodeAt(node, Block.create('lineGroup'))
      },
      insertLineGroupAfter(editor, node) {
        editor.insertNodeAt(node, Block.create('lineGroup'), true)
      },
      insertLineGroup(editor, text) {
        const element = editor.getElement()
        const offset = editor.value.document.nodes.findIndex(node =>
          node.equals(element)
        )

        editor.insertNodeByKey(
          editor.value.document.key,
          offset,
          Block.create({
            type: 'lineGroup',
            nodes: text
              .split('\n')
              .map(line => createElementChild('line', line))
          })
        )
      },
      enforceAdjacentLineGroups(editor, node) {
        const doc = editor.value.document
        const nextSibling = doc.getNextSibling(node.key)
        const prevSibling = doc.getPreviousSibling(node.key)

        if (prevSibling && prevSibling.type !== 'lineGroup') {
          editor.insertLineGroupBefore(node)
        }

        if (!nextSibling || nextSibling.type !== 'lineGroup') {
          editor.insertLineGroupAfter(node)
        }
      }
    },
    queries: {
      isTouchingLineGroup(editor) {
        const prevElement = editor.getPreviousElement()
        const nextElement = editor.getNextElement()
        const prevElementIsLineGroup =
          prevElement && prevElement.type === 'lineGroup'
        const nextElementIsLineGroup =
          nextElement && nextElement.type === 'lineGroup'

        return prevElementIsLineGroup || nextElementIsLineGroup
      }
    }
  }
}

const LineGroupStyled = styled.div`
  display: inline-block;
  width: 844px;
  margin-top: 10px;
  color: #91456f;
  border-radius: 3px;
  line-height: 1.5em;
  ${Element.elementChildPadding} &:hover {
    background-color: #f5f7f9;
  }
  span[contenteditable='false'] {
    color: ${color('pewter')};
    opacity: 1;
  }
  &.highlight-unstructured {
    background: ${color('lightRed')};
    color: ${color('sangria')};
  }
`

const LineGroupWrapper = styled.div`
  position: relative;
  width: 100%;
`

const LineSpan = styled.p`
  margin-top: 0;
  margin-bottom: 0;
`

const Line = props => (
  <LineSpan className="line" {...props}>
    {props.children}
  </LineSpan>
)

class LineGroup extends React.Component {
  state = { showMenuIcon: false }

  onMouseEnter = ev => {
    this.setState({ showMenuIcon: true })
  }

  onMouseLeave = ev => {
    this.setState({ showMenuIcon: false })
  }

  render() {
    return (
      <LineGroupWrapper
        className={'element element-line-group'}
        onMouseEnter={this.onMouseEnter}
        onMouseLeave={this.onMouseLeave}
      >
        <Element.TypeIcon
          name={'ion-ios-plus-outline'}
          show={this.state.showMenuIcon}
          {...this.props}
        />
        <LineGroupStyled
          innerRef={this.props.innerRef}
          className={
            this.props.node.data.get('highlight', false)
              ? 'highlight-unstructured'
              : ''
          }
        >
          {this.props.children}
        </LineGroupStyled>
      </LineGroupWrapper>
    )
  }
}

export default LineGroupPlugin



// WEBPACK FOOTER //
// ./src/components/Editor/plugins/LineGroupPlugin.js